// @ts-check
import { availableMedals } from './stage-goals.js';
/** @typedef {import('./types.js').LevelDefinition} Definition */
/** @typedef {{id:string,score:number,time:number}} Split */
/** @typedef {{splits:Split[],practice:boolean,resumed:boolean,started:boolean,finished:boolean,realTime:number}} Race */
/** @typedef {{bestScore:number,bestTime:number|null,bestRealTime:number|null,splits:Split[]}} Best */
/** @param {boolean} [practice] @returns {Race} */
export function createRace(practice = false) { return { splits: [], practice, resumed: false, started: false, finished: false, realTime: 0 }; }
/** @returns {Best} */
export function createBest() { return { bestScore: 0, bestTime: null, bestRealTime: null, splits: [] }; }
/** Stage results form a ledger. A finished stage is never counted a second time.
 * @param {Race} race @param {import('./scoring.js').Run} run @param {string} id
 */
export function totals(race, run, id) {
  const banked = race.splits.some(s => s.id === id);
  return { score: race.splits.reduce((n, s) => n + s.score, banked ? 0 : run.score), time: race.splits.reduce((n, s) => n + s.time, banked ? 0 : run.elapsed) };
}
/** @param {Race} race @param {Best} best @param {Split} split @param {readonly Definition[]} routes */
export function bankSplit(race, best, split, routes) {
  if (race.finished || race.splits.some(s => s.id === split.id)) return;
  race.splits.push({ ...split });
  if (split.id !== routes[routes.length - 1].id) return;
  race.finished = true;
  if (race.practice || race.splits.length !== routes.length || !race.splits.every((s, i) => s.id === routes[i].id)) return;
  const time = race.splits.reduce((n, s) => n + s.time, 0);
  best.bestScore = Math.max(best.bestScore, race.splits.reduce((n, s) => n + s.score, 0));
  if (race.resumed) return;
  if (best.bestTime === null || time < best.bestTime) { best.bestTime = time; best.splits = race.splits.map(s => ({ ...s })); }
  best.bestRealTime = Math.min(best.bestRealTime ?? Infinity, race.realTime);
}
/** Derived permanent mastery never depends on replay count or repeated rewards.
 * @param {{[id:string]:import('./scoring.js').Record}} records @param {readonly Definition[]} routes
 */
export function mastery(records, routes) {
  const medals = routes.reduce((n, d) => n + availableMedals(d).filter(m => records[d.id]?.medals.includes(m)).length, 0), maximum = routes.reduce((n, d) => n + availableMedals(d).length, 0);
  const ranks = [{ at: 0, name: 'Circuit scout' }, { at: .125, name: 'Grid runner' }, { at: .375, name: 'System ace' }, { at: .625, name: 'Core legend' }, { at: 1, name: 'Full mastery' }];
  const rank = [...ranks].reverse().find(r => medals >= Math.ceil(maximum * r.at));
  const next = ranks.find(r => medals < Math.ceil(maximum * r.at));
  const timed = routes.filter(d => typeof records[d.id]?.bestTime === 'number');
  return { medals, maximum, rank: rank?.name ?? 'Circuit scout', next: next ? { name: next.name, remaining: Math.ceil(maximum * next.at) - medals } : null,
    sumOfBest: timed.reduce((n, d) => n + /** @type {number} */(records[d.id].bestTime), 0), timed: timed.length };
}
const duration = (/** @type {unknown} */ n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 32 * 359999;
const score = (/** @type {unknown} */ n) => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 && n <= 32 * 99999999;
/** Validate consecutive, unique splits against the actual content registry.
 * @param {unknown} raw @param {readonly Definition[]} routes @returns {Split[]|null}
 */
function readSplits(raw, routes) {
  if (!Array.isArray(raw) || raw.length > routes.length) return null;
  const start = raw.length ? routes.findIndex(d => d.id === raw[0]?.id) : 0;
  if (start < 0 || !raw.every((s, i) => s && s.id === routes[start + i]?.id && score(s.score) && s.score <= 99999999 && duration(s.time) && s.time <= 359999)) return null;
  return raw.map(s => ({ id: s.id, score: s.score, time: s.time }));
}
/** Old saves retain their score and unlocks but cannot invent earlier campaign splits.
 * @param {unknown} raw @param {readonly Definition[]} routes @param {Definition} current @returns {Race|null}
 */
export function readRace(raw, routes, current) {
  if (raw === undefined) { const race = createRace(); race.resumed = true; return race; }
  if (!raw || typeof raw !== 'object') return null;
  const r = /** @type {Race} */ (raw), splits = readSplits(r.splits, routes);
  if (!splits || !duration(r.realTime) || !['practice', 'resumed', 'started', 'finished'].every(k => typeof r[/** @type {'practice'|'resumed'|'started'|'finished'} */(k)] === 'boolean')) return null;
  const index = routes.findIndex(d => d.id === current.id), last = splits[splits.length - 1];
  if (last && last.id !== current.id && last.id !== routes[index - 1]?.id || !r.practice && !r.resumed && splits.length && splits[0].id !== routes[0].id || r.finished && last?.id !== routes[routes.length - 1].id || !r.started && (splits.length || r.realTime)) return null;
  return { splits, realTime: r.realTime, practice: r.practice, resumed: r.resumed, started: r.started, finished: r.finished };
}
/** @param {unknown} raw @param {readonly Definition[]} routes @returns {Best|null} */
export function readBest(raw, routes) {
  if (raw === undefined) return createBest();
  if (!raw || typeof raw !== 'object') return null;
  const b = /** @type {Best} */ (raw), splits = readSplits(b.splits, routes);
  if (!splits || !score(b.bestScore) || b.bestTime !== null && !duration(b.bestTime) || b.bestRealTime !== null && !duration(b.bestRealTime) || b.bestTime === null && splits.length || b.bestTime !== null && (splits.length !== routes.length || Math.abs(splits.reduce((n, s) => n + s.time, 0) - b.bestTime) > .001)) return null;
  return { bestScore: b.bestScore, bestTime: b.bestTime, bestRealTime: b.bestRealTime, splits };
}
